import { HighlightOff, Info } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ClockCheck from 'components/Icons/ClockCheck';
import CommonlandsLogoColorful from 'components/Icons/CommonlandsLogoColorful';
import CopyRight from 'components/Icons/CopyRight';
import dayjs from 'dayjs';
import useShallowEqualSelector from 'hooks/useShallowEqualSelector';
import React from 'react';
import { useDispatch } from 'react-redux';
import { show } from 'redux/reducers/snackbar';
import PaidCheckSvg from 'assets/paid.svg';

const InvoiceInModal = ({ invoices, onClickCancel, loadingCancel }) => {
  const publicUser = useShallowEqualSelector((state) => state.publicUser).user;
  const dispatch = useDispatch();
  const isPaid = invoices?.status === 'paid';
  const isPendingPaid = invoices?.status === 'wait_for_paying';
  const isCancelled = invoices?.status === 'cancelled';
  const bucket = invoices?.bucket;

  const items = [
    {
      label: 'Bank',
      value: 'Centenary Bank',
    },
    {
      label: 'Payer name',
      value: publicUser?.fullName,
    },
    {
      label: 'Description',
      value: `${bucket?.credits} credits / ${bucket?.price.toLocaleString()} UGX`,
    },
    {
      label: 'Date of issue',
      value: dayjs(invoices?.createdAt).format('MMM DD, YYYY'),
    },
    {
      label: 'Date paid',
      value: invoices?.paidAt
        ? dayjs(invoices?.paidAt).format('MMM DD, YYYY')
        : '',
    },
    {
      type: 'Divider',
      value: 'Divider',
    },
    {
      label: 'Amount',
      value: `${bucket?.price.toLocaleString()} UGX`,
      valueProps: {
        fontSize: 18,
        color: 'rgba(0, 128, 58, 1)',
      },
    },
  ];

  const onClickCopy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      dispatch(show({ message: 'Copied', severity: 'success' }));
    } catch (error) {
      dispatch(
        show({
          message: 'Can not copy text as your connection http is not secure',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box>
      <Box bgcolor={'white'} px={'20px'} py={'30px'} borderRadius={'12px'}>
        <CommonlandsLogoColorful width={52} height={62} />
        <Typography fontWeight={700} fontSize={21} mt={2}>
          Invoice
        </Typography>
        <Typography>
          Invoice Number{' '}
          <Typography component={'span'} fontWeight={600}>
            {invoices?.invoiceNumber}
          </Typography>
        </Typography>
        {isPendingPaid && (
          <Chip
            label="Pending"
            icon={<ClockCheck color="rgba(219, 153, 11, 1)" />}
            sx={{
              px: '8px',
              py: '5px',
              fontWeight: 500,
              bgcolor: 'rgba(250, 189, 58, 0.2)',
              color: 'rgba(234, 163, 0, 1)',
              mt: '10px',
              mb: '40px',
            }}
          />
        )}
        {isCancelled && (
          <Box py={'10px'}>
            <Chip
              label="Cancelled"
              icon={<HighlightOff color="white" />}
              sx={{
                px: '8px',
                py: '5px',
                fontWeight: 500,
                bgcolor: 'rgb(249, 119, 110)',
                color: 'white',
                mt: '10px',
                mb: '40px',
              }}
            />
          </Box>
        )}
        {isPaid && (
          <Box py={'30px'}>
            <Box
              component={'img'}
              src={PaidCheckSvg}
              position={'absolute'}
              right={40}
              top={80}
            ></Box>
          </Box>
        )}
        {}
        <Stack gap={'16px'} mt={'15px'}>
          {items
            .filter((item) => item?.value?.length > 0)
            .map((item, index) => {
              if (item.type === 'Divider') {
                return (
                  <Box
                    key={index}
                    sx={{
                      // dashed divider
                      border: '1px dashed #E6E6E6',
                      borderRadius: '8px',
                      height: '1px',
                      width: '100%',
                    }}
                  />
                );
              }
              return (
                <Box
                  key={index}
                  display={'flex'}
                  justifyContent={'space-between'}
                  gap={2}
                >
                  <Typography
                    color={'rgba(112, 112, 112, 1)'}
                    minWidth={'100px'}
                  >
                    {item.label}
                  </Typography>
                  <Typography fontWeight={600} {...item?.valueProps}>
                    {item.value}
                  </Typography>
                </Box>
              );
            })}
        </Stack>
        {isPendingPaid && (
          <Box display={'flex'} justifyContent={'flex-end'} mt={'20px'}>
            <Button
              variant="outlined"
              color="error"
              disabled={loadingCancel}
              sx={{
                width: '150px',
                height: '40px',
              }}
              onClick={onClickCancel}
            >
              {loadingCancel ? (
                <CircularProgress color="error" size={20} />
              ) : (
                'Cancel Invoice'
              )}
            </Button>
          </Box>
        )}
      </Box>
      {isPendingPaid && (
        <Box bgcolor={'white'} borderRadius={'12px'} mt={2}>
          <Box display={'flex'} p={'20px'} gap={'15px'}>
            <Info color="primary" />
            <Typography>
              To make a payment, the user needs to visit a{' '}
              <Typography component={'span'} fontWeight={700}>
                Centenary Bank Agent
              </Typography>
              , present their{' '}
              <Typography component={'span'} fontWeight={700}>
                Reference ID
              </Typography>{' '}
              to the agent, and proceed with the payment.
            </Typography>
          </Box>
          <Box
            bgcolor={'rgba(94, 196, 172, 0.1)'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            py={'5px'}
            color={'rgba(52, 143, 121, 1)'}
            gap={'6px'}
          >
            <Typography fontWeight={'600'}>
              Reference ID: {publicUser?.customerReference}
            </Typography>
            <IconButton
              color="primary"
              sx={{
                p: '2px',
              }}
              onClick={() => onClickCopy(publicUser?.customerReference)}
            >
              <CopyRight />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InvoiceInModal;
